import { z } from "zod";

/** Limits use the bytes of JSON.stringify(value), including quotes and escapes. */
export const INDUCTION_MAX_GROUP_JSON_BYTES = 150_000;
export const INDUCTION_MAX_TITLE_JSON_BYTES = 8_000;
export const INDUCTION_MAX_ADDRESS_JSON_BYTES = 40_000;

/** Pure UTF-8 byte count; works in web, Node and React Native without Buffer. */
export function jsonEncodedUtf8Bytes(value: string): number {
  const json = JSON.stringify(value);
  let bytes = 0;
  for (let index = 0; index < json.length; index++) {
    const code = json.charCodeAt(index);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && index + 1 < json.length) {
      const next = json.charCodeAt(index + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        index++;
      } else bytes += 3;
    } else bytes += 3;
  }
  return bytes;
}

// Four contact fields render as ONE newline-joined principal-contractor group.
// JSON encoding of their joined value cannot exceed the sum of their own
// encodings, so this cap composes even when every field is populated.
export const INDUCTION_MAX_CONTRACTOR_PART_JSON_BYTES =
  INDUCTION_MAX_GROUP_JSON_BYTES / 4;

export const PROJECT_INDUCTION_TEXT_LIMITS = {
  title: INDUCTION_MAX_TITLE_JSON_BYTES,
  siteAddress: INDUCTION_MAX_ADDRESS_JSON_BYTES,
  siteRules: INDUCTION_MAX_GROUP_JSON_BYTES,
  welfareArrangements: INDUCTION_MAX_GROUP_JSON_BYTES,
  fireStrategy: INDUCTION_MAX_GROUP_JSON_BYTES,
  principalContractor: INDUCTION_MAX_CONTRACTOR_PART_JSON_BYTES,
  principalContractorCompany: INDUCTION_MAX_CONTRACTOR_PART_JSON_BYTES,
  principalContractorPhone: INDUCTION_MAX_CONTRACTOR_PART_JSON_BYTES,
  principalContractorEmail: INDUCTION_MAX_CONTRACTOR_PART_JSON_BYTES,
  firstAidArrangements: INDUCTION_MAX_GROUP_JSON_BYTES,
  nearestHospital: INDUCTION_MAX_GROUP_JSON_BYTES,
  emergencyProcedures: INDUCTION_MAX_GROUP_JSON_BYTES,
  assemblyPoint: INDUCTION_MAX_GROUP_JSON_BYTES,
} as const;

export type ProjectInductionTextField = keyof typeof PROJECT_INDUCTION_TEXT_LIMITS;

export function projectInductionTextField(field: ProjectInductionTextField) {
  return z.string().refine(
    (value) =>
      jsonEncodedUtf8Bytes(value) <= PROJECT_INDUCTION_TEXT_LIMITS[field],
    { message: `Shorten ${field} before saving; the site induction cannot retain more text` },
  );
}

/** For imported/copied project values that do not pass a request schema. */
export function oversizedProjectInductionFields(
  values: Record<string, unknown>,
): ProjectInductionTextField[] {
  return (Object.keys(PROJECT_INDUCTION_TEXT_LIMITS) as ProjectInductionTextField[])
    .filter((field) => {
      const value = values[field];
      return typeof value === "string" &&
        jsonEncodedUtf8Bytes(value) > PROJECT_INDUCTION_TEXT_LIMITS[field];
    });
}
